(() => {
  function aomTrack(n, p) { window.aomTrack?.(n, p); }

  const KEY = Symbol('myApp:stickyATC');
  const controllers = new WeakMap();

  let OWNER = null;

  // Prefer Dawn's main PDP form, skip quick-add modal forms
  const isQuickAddForm = (formEl) => !!formEl.closest('quick-add-modal');

  // Target a more specific submitter if available
  const SUBMITTER_SELECTOR =
    'button[name="add"][type="submit"], input[name="add"][type="submit"], button[type="submit"], input[type="submit"]';

  class StickyATCController {
    constructor(form) {
      this.form = form;
      this.signalBag = new AbortController();
      this.observers = [];
      this.isAddToCartOut = false;
      this.isFooterInView = false;
      this.isCartOpen = false;
      this._ui = null; // cache inner sticky UI
      this._price = null; // cache inner sticky price
      this._variant = null; // cache inner sticky variant title
      this._images = null; // cache image stack wrapper
      this._img1 = null;
      this._img2 = null;
      this._imgMore = null;
      this._bundleActive = false; // once a bundle event fires, it owns the price
      this._btnDefaultHTML = null; // button's resting markup (label + price spans)
      this._lastPriceText = null; // survives the loading-state HTML swap
    }

    get stickyWrap() {
      return document.querySelector('#ao-stickyATC');
    }
    get uiButton() {
      return (this._ui ||= this.stickyWrap?.querySelector('.ao-add-to-cart-button'));
    }
    get priceEl() {
      return (this._price ||= this.stickyWrap?.querySelector('[data-ao-price]'));
    }
    get variantEl() {
      return (this._variant ||= this.stickyWrap?.querySelector('[data-ao-variant]'));
    }
    get imagesEl() {
      return (this._images ||= this.stickyWrap?.querySelector('[data-ao-images]'));
    }
    get img1El() {
      return (this._img1 ||= this.stickyWrap?.querySelector('[data-ao-img-1]'));
    }
    get img2El() {
      return (this._img2 ||= this.stickyWrap?.querySelector('[data-ao-img-2]'));
    }
    get imgMoreEl() {
      return (this._imgMore ||= this.stickyWrap?.querySelector('[data-ao-img-more]'));
    }

    init() {
      if (window.innerWidth > 768) return false;
      if (isQuickAddForm(this.form)) return false;
      if (OWNER && OWNER !== this) return false;

      this.submitBtn = this.form.querySelector(SUBMITTER_SELECTOR);
      this._ensureStickyInBody();

      if (!this.stickyWrap || !this.uiButton || !this.submitBtn) return false;

      this._btnDefaultHTML = this.uiButton.innerHTML;
      OWNER = this;
      this._bind();
      this._measure();
      this._observeButton();
      this._observeFooter();
      this._observeSubmitState();
      this._initPrice();
      this._initVariant();
      this._observeCartOverlay();
      this._toggle();
      return true;
    }

    // Mirrors Dawn's own price element so the sticky bar never computes a
    // price itself (avoids re-deriving variant/market pricing). A bundle
    // tier pick (aom:savings) reflects the full multi-item total instead,
    // and takes over permanently once seen, since it's more specific than
    // the native single-variant price.
    _initPrice() {
      if (!this.priceEl) return;

      const nativePrice = this.form.closest('product-info')?.querySelector('.price');
      if (nativePrice) {
        // Sync immediately: the Liquid default (money filter) can format
        // differently than Dawn's own price markup (e.g. currency_code_enabled),
        // so wait for a real mutation and the two would visibly disagree for a beat.
        this._syncNativePrice(nativePrice);
        const mo = new MutationObserver(() => {
          if (!this._bundleActive) this._syncNativePrice(nativePrice);
        });
        mo.observe(nativePrice, { childList: true, subtree: true, characterData: true });
        this.observers.push(mo);
      }

      document.addEventListener(
        'aom:savings',
        (e) => {
          const d = e.detail || {};
          if (typeof d.priceCents !== 'number') return;
          this._bundleActive = true;
          this._lastPriceText = this._formatMoney(d.priceCents);
          this.priceEl.textContent = this._lastPriceText;
          if (Array.isArray(d.images)) this._setImages(d.images, d.quantity);
          // d.label is the merchant's own (already-translated) offer title —
          // prefer it. Only fall back to a hardcoded English string when the
          // offer has no title configured, same fallback bundle.js itself
          // uses for its own tier labels.
          if (this.variantEl) {
            if (d.quantity > 1) {
              this.variantEl.textContent = d.label || (d.quantity + ' items');
            } else if (Array.isArray(d.titles) && d.titles[0]) {
              // qty-1 tier: show the actual variant name (e.g. "Blush"), same
              // as a plain (non-bundle) variant switch would.
              this.variantEl.textContent = d.titles[0];
            } else if (d.label) {
              this.variantEl.textContent = d.label;
            }
          }
        },
        { signal: this.signalBag.signal }
      );

      // bundle.js may have already picked a tier and dispatched aom:savings
      // before this controller finished its async (whenDefined) init — ask
      // it to replay the current state so we don't show the SSR single-item
      // fallback until the shopper happens to touch the bundle widget.
      document.dispatchEvent(new CustomEvent('aom:savings:request'));
    }

    // Single image by default; a bundle tier can pick several unit images —
    // stack up to 2 and badge the rest ("+N") rather than growing sideways.
    _setImages(urls, quantity) {
      if (!this.imagesEl || !this.img1El || !this.img2El) return;
      const unique = urls.filter(Boolean).map(this._toSizedUrl);
      if (!unique.length) return;
      this.img1El.src = unique[0];
      this.img1El.hidden = false;
      if (unique.length >= 2) {
        this.img2El.src = unique[1];
        this.img2El.hidden = false;
        this.imagesEl.classList.add('is-stacked');
      } else {
        this.img2El.hidden = true;
        this.imagesEl.classList.remove('is-stacked');
      }
      const shown = Math.min(unique.length, 2);
      const extra = Math.max(0, (quantity || unique.length) - shown);
      if (this.imgMoreEl) {
        this.imgMoreEl.hidden = extra <= 0;
        if (extra > 0) this.imgMoreEl.textContent = '+' + extra;
      }
    }

    // Raw Shopify CDN URLs are full-size originals; ask the CDN to resize
    // for this 44×44 slot instead of shipping the full asset.
    _toSizedUrl(u) {
      if (!u) return u;
      try {
        const url = new URL(u, window.location.origin);
        if (!url.searchParams.has('width')) url.searchParams.set('width', '88');
        return url.toString();
      } catch (_) {
        return u;
      }
    }

    _syncNativePrice(container) {
      const candidates = container.querySelectorAll('.price-item--sale, .price-item--regular');
      for (const el of candidates) {
        if (el.closest('s')) continue; // skip struck-through "was" prices
        if (el.offsetParent !== null) {
          this._lastPriceText = el.textContent.trim();
          this.priceEl.textContent = this._lastPriceText;
          return;
        }
      }
    }

    _formatMoney(cents) {
      const s = window.Shopify;
      const currency = (s && s.currency && s.currency.active) || 'USD';
      const locale = document.documentElement.lang || (s && s.locale) || undefined;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (_) {
        return (cents / 100).toFixed(2) + ' ' + currency;
      }
    }

    // Mirrors the option picker's selection into the sticky bar's variant
    // label. Reads Dawn's native variant-selects/variant-radios only (not
    // the bundle widget's own per-unit swatches, which have no single
    // "selected variant" to report).
    _initVariant() {
      if (!this.variantEl) return;
      const dataEl = document.querySelector('[data-ao-variants-json]');
      if (!dataEl) return;

      let variants;
      try {
        variants = JSON.parse(dataEl.textContent || '[]');
      } catch (e) {
        return;
      }

      document.addEventListener(
        'change',
        (e) => {
          if (e.target.closest('.aom-discount-wrapper')) return;
          const picker = e.target.closest('variant-selects, variant-radios');
          if (!picker) return;

          const selects = picker.querySelectorAll('select');
          let options;
          if (selects.length) {
            options = Array.from(selects).map((s) => s.value);
          } else {
            const groups = {};
            picker.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
              groups[r.name] = r.value;
            });
            options = Object.values(groups);
          }

          const match = variants.find((v) => v.options.every((o, i) => o === options[i]));
          if (!match) return;
          this.variantEl.textContent = match.title;
          // Bundle image stack takes priority; a plain variant switch only
          // matters for the single-image (non-bundle) state.
          if (!this._bundleActive && match.featured_image?.src) {
            this._setImages([match.featured_image.src], 1);
          }
        },
        { signal: this.signalBag.signal }
      );
    }

    // Hide behind the cart drawer/notification while it's open, so the
    // sticky bar doesn't sit on top of the drawer's own checkout button.
    _observeCartOverlay() {
      const drawer = document.querySelector('cart-drawer');
      const notification = document.getElementById('cart-notification');
      const targets = [drawer, notification].filter(Boolean);
      if (!targets.length) return;

      const check = () => {
        this.isCartOpen = targets.some((el) => el.classList.contains('active'));
        this._toggle();
      };
      const mo = new MutationObserver(check);
      targets.forEach((el) => mo.observe(el, { attributes: true, attributeFilter: ['class'] }));
      this.observers.push(mo);
      check();
    }

    _ensureStickyInBody() {
      const el = this.stickyWrap;
      if (!el) return;
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    }

    _bind() {
      const s = this.signalBag.signal;

      // Click -> behave like real Add-to-Cart click
      this.uiButton.addEventListener(
        'click',
        () => {
          aomTrack('aom_floating_atc_click', {
            ui_element: 'floating_add_to_cart',
            action_type: 'add_to_cart',
            variant_id: this.form.querySelector('[name="id"]')?.value || '',
          });
          // mirror disabled while we hand off to the native flow
          this.uiButton.setAttribute('aria-disabled', 'true');
          this.uiButton.setAttribute('disabled', '');

          // Show loading state immediately; _syncDisabled will restore when done
          if (!this.uiButton.classList.contains('ao-btn-loading')) {
            this.uiButton.classList.add('ao-btn-loading');
            const loadingLabel = this.uiButton.dataset.loadingLabel || '';
            this.uiButton.innerHTML =
              '<span class="ao-spinner" aria-hidden="true"></span>' + loadingLabel;
          }

          // Only submit if the native button is actually enabled
          const isDisabled =
            this.submitBtn.getAttribute('aria-disabled') === 'true' ||
            this.submitBtn.disabled === true ||
            this.submitBtn.classList.contains('loading');

          if (!isDisabled) {
            if (typeof this.form.requestSubmit === 'function') {
              this.form.requestSubmit(this.submitBtn);
            } else {
              this.submitBtn.click();
            }
          }

          // Re-enable will happen via _observeSubmitState when Dawn removes 'loading'
          // but add a safety re-enable after 4s in case nothing fires.
          setTimeout(() => this._syncDisabled(), 4000);
        },
        { signal: s }
      );

      const onScroll = () => this._toggle();
      const onResize = () => {
        this._measure();
        this._toggle();
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal: s });
      window.addEventListener('resize', onResize, { signal: s });
      window.addEventListener('orientationchange', onResize, { signal: s });

      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(() => {
          this._measure();
          this._toggle();
        });
        ro.observe(this.form);
        this.observers.push(ro);
      }
    }

    _measure() {
      const rect = this.submitBtn.getBoundingClientRect();
      this.addToCartTop = rect.top + window.scrollY;
    }

    _observeButton() {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            this.isAddToCartOut = !e.isIntersecting;
          }
          this._toggle();
        },
        { threshold: 0 }
      );
      io.observe(this.submitBtn);
      this.observers.push(io);
    }

    _observeFooter() {
      if (!('IntersectionObserver' in window)) return;
      const footer = document.querySelector('footer');
      if (!footer) return;
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            this.isFooterInView = e.isIntersecting;
          }
          this._toggle();
        },
        { threshold: 0 }
      );
      io.observe(footer);
      this.observers.push(io);
    }

    _observeSubmitState() {
      const sync = () => this._syncDisabled();
      this._btnObserver = new MutationObserver(sync);
      this._btnObserver.observe(this.submitBtn, {
        attributes: true,
        attributeFilter: ['aria-disabled', 'disabled', 'class'],
      });
      sync();
    }

    _syncDisabled() {
      if (!this.uiButton) return;
      const isLoading = this.submitBtn.classList.contains('loading');
      const disabled =
        this.submitBtn.getAttribute('aria-disabled') === 'true' ||
        this.submitBtn.disabled === true ||
        isLoading;
      this.uiButton.toggleAttribute('disabled', disabled);
      this.uiButton.setAttribute('aria-disabled', String(disabled));
      // Sync loading visual with Dawn's native loading state
      if (isLoading) {
        if (!this.uiButton.classList.contains('ao-btn-loading')) {
          this.uiButton.classList.add('ao-btn-loading');
          const loadingLabel = this.uiButton.dataset.loadingLabel || '';
          this.uiButton.innerHTML =
            '<span class="ao-spinner" aria-hidden="true"></span>' + loadingLabel;
        }
      } else if (this.uiButton.classList.contains('ao-btn-loading')) {
        this.uiButton.classList.remove('ao-btn-loading');
        // Restore the label + price spans (not a plain label) so the price
        // survives a loading round-trip; re-cache the price node since the
        // old one was just detached by the innerHTML swap.
        this.uiButton.innerHTML = this._btnDefaultHTML;
        this._price = null;
        if (this._lastPriceText && this.priceEl) this.priceEl.textContent = this._lastPriceText;
      }
    }

    _toggle() {
      if (!this.stickyWrap) return;
      // Yield to the Sticky Bundle Bar when it is active — don't fight for the bottom slot.
      if (document.getElementById('sticky-bundle-bar')?.classList.contains('sbb-active')) {
        this.stickyWrap.classList.remove('show');
        return;
      }
      const sc = window.scrollY;
      const shouldShow =
        this.isAddToCartOut && sc > (this.addToCartTop || 0) && !this.isFooterInView && !this.isCartOpen;
      this.stickyWrap.classList.toggle('show', shouldShow);
    }

    destroy() {
      if (OWNER === this) OWNER = null;
      this.signalBag.abort();
      this.observers.forEach((o) => o.disconnect());
      this._btnObserver?.disconnect();
      this.stickyWrap?.classList.remove('show');
      this.uiButton?.removeAttribute('disabled');
      this.uiButton?.removeAttribute('aria-disabled');
    }
  }

  function enhance(form) {
    if (controllers.has(form)) return;
    const ctrl = new StickyATCController(form);
    if (!ctrl.init()) return;
    controllers.set(form, ctrl);
    Object.defineProperty(form, KEY, { value: true });
  }

  function cleanup(node) {
    const ctrl = controllers.get(node);
    if (ctrl) {
      ctrl.destroy();
      controllers.delete(node);
    }
  }

  customElements.whenDefined('product-form').then(() => {
    document.querySelectorAll('product-form').forEach(enhance);

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.('product-form')) enhance(n);
          n.querySelectorAll?.('product-form')?.forEach(enhance);
        });
        m.removedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.('product-form')) cleanup(n);
          n.querySelectorAll?.('product-form')?.forEach(cleanup);
        });
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  });
})();

/* ============================================================================
   Sticky Bundle Bar — Intent-Based Merchandising
   Activated only when the user arrived from an anchor product page via a
   "View Details" link that carried bundle context in the URL.
   All other visitors (organic, direct, etc.) see the standard ATC as usual.
   ========================================================================== */
(() => {
  'use strict';

  function aomTrack(n, p) { window.aomTrack?.(n, p); }

  const INTENT_KEY   = 'bundle_intent_v1';
  const TTL_MS       = 60 * 60 * 1000; // 60 minutes
  const ACTIVE_CLASS = 'sbb-active';
  const LOADING_CLASS = 'is-loading';
  const INIT_GUARD   = '__sbb_init';

  // ── Cart helper ─────────────────────────────────────────────────────────────────────────
  // POSTs items to /cart/add.js, requests Dawn's section HTML in the same call
  // (so the response already has fresh cart content), then renders + opens the
  // cart drawer / notification without a second round-trip.
  async function addItemsAndOpenCart(items) {
    const target =
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification');

    // Ask Shopify to render the sections Dawn needs inside the same response.
    const sectionIds =
      target?.getSectionsToRender?.().map((s) => s.section || s.id) ?? [];

    const body = { items };
    if (sectionIds.length) {
      body.sections      = sectionIds;
      body.sections_url  = window.location.pathname;
    }

    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`/cart/add.js ${res.status}`);

    const parsedState = await res.json();

    // Let Dawn paint the fresh cart content then open the drawer.
    // renderContents() calls open() inside a setTimeout. Dawn's product-form.js
    // removes 'is-empty' from cart-drawer synchronously (in its finally block)
    // so it's gone before that setTimeout fires. We must do the same here.
    if (target) {
      if (target.classList.contains('is-empty')) target.classList.remove('is-empty');
      if (typeof target.renderContents === 'function' && parsedState.sections) {
        target.renderContents(parsedState); // open() is called inside renderContents
      } else {
        target.open?.();
      }
    }

    // Bubble a refresh event for any other widgets listening.
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );

    return parsedState;
  }

  // ── Money ─────────────────────────────────────────────────────────────────
  function formatMoney(cents) {
    const currency =
      (typeof window.Shopify !== 'undefined' && window.Shopify.currency?.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency,
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2) + '\u00a0' + currency;
    }
  }

  // ── URL ───────────────────────────────────────────────────────────────────
  function urlParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  // ── sessionStorage ────────────────────────────────────────────────────────
  function readIntent() {
    try { return JSON.parse(sessionStorage.getItem(INTENT_KEY)); } catch (_) { return null; }
  }
  function writeIntent(payload) {
    try { sessionStorage.setItem(INTENT_KEY, JSON.stringify(payload)); } catch (_) {}
  }
  function clearIntent() {
    try { sessionStorage.removeItem(INTENT_KEY); } catch (_) {}
  }
  function dismissKey(anchorId, companionId) {
    return `bundle_dismissed_v1:${anchorId}:${companionId}`;
  }
  function isDismissed(anchorId, companionId) {
    try { return sessionStorage.getItem(dismissKey(anchorId, companionId)) === 'true'; } catch (_) { return false; }
  }
  function setDismissed(anchorId, companionId) {
    try { sessionStorage.setItem(dismissKey(anchorId, companionId), 'true'); } catch (_) {}
  }

  // ── Page meta ─────────────────────────────────────────────────────────────
  function readMeta() {
    const node = document.getElementById('bundle-meta-data');
    if (!node) return null;
    const raw = node.textContent;
    try {
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  }

  function findDeal(meta, anchorProductId, offerId) {
    if (!meta?.deals) return null;
    // offerId (UUID) is the authoritative key — match on it alone when present.
    // Fall back to anchorProductId match if no offerId.
    if (offerId) {
      return meta.deals.find((d) => String(d.offerId) === String(offerId)) || null;
    }
    return meta.deals.find((d) => String(d.anchorProductId) === String(anchorProductId)) || null;
  }

  // ── Resolve active intent ─────────────────────────────────────────────────
  // URL params always win. Falls back to sessionStorage.
  // Returns { intent, deal } or null.
  function resolveIntent(meta) {
    const urlAnchorId  = urlParam('bundle_anchor_product_id');
    const urlVariantId = urlParam('bundle_anchor_variant_id');
    const urlOfferId   = urlParam('bundle_rule_id');

    if (urlAnchorId && urlOfferId) {
      const deal = findDeal(meta, urlAnchorId, urlOfferId);
      if (!deal) return null; // URL params present but don't match this page — abort
      const now = Date.now();
      const intent = {
        anchorProductId:    urlAnchorId,
        anchorVariantId:    urlVariantId || '',
        companionProductId: String(meta.companionProductId),
        bundleRuleId:       urlOfferId,
        createdAt:          now,
        expiresAt:          now + TTL_MS,
      };
      writeIntent(intent);
      return { intent, deal };
    }

    // Fallback: sessionStorage
    const stored = readIntent();
    if (!stored) return null;
    if (Date.now() > stored.expiresAt) { clearIntent(); return null; }
    if (String(stored.companionProductId) !== String(meta.companionProductId)) { clearIntent(); return null; }
    const deal = findDeal(meta, stored.anchorProductId, stored.bundleRuleId);
    if (!deal) { clearIntent(); return null; }
    return { intent: stored, deal };
  }

  // ── UI ────────────────────────────────────────────────────────────────────
  // async: fetches anchor product data when Liquid couldn't resolve the image
  // (e.g. anchorProductHandle was blank in the metafield — old data pre-republish).
  async function showBar(deal) {
    const bar = document.getElementById('sticky-bundle-bar');
    if (!bar) return;

    let imageUrl  = deal.anchorImageUrl || null;
    let title     = deal.anchorTitle   || '';

    // JS fallback: if Liquid produced no image/title (empty handle in metafield),
    // fetch from the storefront AJAX API using the handle stored in the JSON.
    if ((!imageUrl || !title) && deal.anchorProductHandle) {
      try {
        const localeParam = window.Shopify?.locale ? '?locale=' + window.Shopify.locale : '';
        const data = await fetch('/products/' + deal.anchorProductHandle + '.js' + localeParam)
          .then((r) => (r.ok ? r.json() : Promise.reject(r)));
        if (!imageUrl) imageUrl = data.featured_image || (data.images && data.images[0]) || null;
        if (!title)    title    = data.title || deal.anchorProductHandle;
      } catch (err) {
        // AJAX fallback failed — bar will show without image/title
      }
    }

    bar.querySelector('.sbb-image').src                      = imageUrl || '';
    bar.querySelector('.sbb-image').alt                      = title;
    bar.querySelector('.sbb-badge').textContent              = deal.badgeText || '';
    bar.querySelector('.sbb-anchor-title').textContent       = title;
    bar.querySelector('.sbb-original-price').textContent     = formatMoney(deal.bundleOriginalPriceCents);
    bar.querySelector('.sbb-bundle-price-value').textContent = formatMoney(deal.bundlePriceCents);

    // Schedule class addition on the next animation frame so the browser has
    // committed a paint with translateY(100%) before we transition to translateY(0).
    requestAnimationFrame(() => {
      bar.classList.add(ACTIVE_CLASS);
      bar.removeAttribute('aria-hidden');
    });
  }

  function hideBar() {
    const bar = document.getElementById('sticky-bundle-bar');
    if (!bar) return;
    bar.classList.remove(ACTIVE_CLASS);
    bar.setAttribute('aria-hidden', 'true');
  }

  // ── Anchor-page link augmentation ─────────────────────────────────────────
  // Appends bundle URL params to [data-bundle-view-details] links on click.
  // Also passes the currently selected addon variant as ?variant= so the
  // user lands on the exact variant they had chosen in the cross-sell card.
  function augmentAnchorLinks() {
    document.querySelectorAll('[data-bundle-view-details]').forEach((link) => {
      if (link.__sbb_augmented) return;
      link.__sbb_augmented = true;
      link.addEventListener('click', () => {
        const anchorProductId = link.dataset.anchorProductId;
        const offerId         = link.dataset.bundleRuleId;
        // Read the live variant selects inside the card, not stale data attrs.
        const card            = link.closest('.aom-cs-card');
        const anchorVariantId = card?.querySelector('.aom-cs-anchor-variant-select')?.value || '';
        // Addon variant select shares .aom-cs-variant-select but is NOT the anchor one.
        const addonVariantRaw = card?.querySelector('.aom-cs-variant-select:not(.aom-cs-anchor-variant-select)')?.value || '';
        // Only use the value if it's a numeric ID (guards against "Loading…" placeholder).
        const addonVariantId  = /^\d+$/.test(addonVariantRaw) ? addonVariantRaw : '';

        const url = new URL(link.href, window.location.origin);

        // Always navigate to the selected addon variant so the PDP opens on the
        // right one rather than the product's default.
        if (addonVariantId) {
          url.searchParams.set('variant', addonVariantId);
        }

        // Pass bundle context for the SBB when all required pieces are present.
        if (anchorProductId && anchorVariantId && offerId) {
          url.searchParams.set('bundle_anchor_product_id', anchorProductId);
          url.searchParams.set('bundle_anchor_variant_id', anchorVariantId);
          url.searchParams.set('bundle_rule_id', offerId);
        }

        link.href = url.toString();
      });
    });
  }

  // ── Companion-page handlers ───────────────────────────────────────────────
  function bindHandlers(intent, deal) {
    const bar = document.getElementById('sticky-bundle-bar');
    if (!bar) return;
    const companionId = intent.companionProductId;

    const closeBtn = bar.querySelector('.sbb-close');
    if (closeBtn && !closeBtn.__sbb_bound) {
      closeBtn.__sbb_bound = true;
      closeBtn.addEventListener('click', () => {
        aomTrack('aom_sbb_dismiss', {
          ui_element: 'sticky_bundle_bar',
          action_type: 'dismiss',
          anchor_product_id: intent.anchorProductId,
          companion_product_id: companionId,
        });
        setDismissed(intent.anchorProductId, companionId);
        hideBar();
        // Standard ATC will resume on next scroll event naturally
      });
    }

    const ctaBtn = bar.querySelector('.sbb-btn-primary');
    if (ctaBtn && !ctaBtn.__sbb_bound) {
      ctaBtn.__sbb_bound = true;
      ctaBtn.addEventListener('click', async () => {
        if (ctaBtn.disabled) return;
        const companionVariantId = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
        if (!intent.anchorVariantId || !companionVariantId) {
          console.error('[StickyBundleBar] Missing variant ID — aborting.');
          return;
        }
        const originalHTML = ctaBtn.innerHTML;
        const loadingLabel = ctaBtn.dataset.loadingLabel || 'Adding…';
        aomTrack('aom_sbb_add_both', {
          ui_element: 'sticky_bundle_bar',
          action_type: 'add_both_to_cart',
          anchor_variant_id: intent.anchorVariantId,
          companion_variant_id: companionVariantId,
          bundle_rule_id: deal.bundleRuleId,
        });
        ctaBtn.disabled = true;
        ctaBtn.classList.add(LOADING_CLASS);
        ctaBtn.innerHTML = '<span class="sbb-spinner" aria-hidden="true"></span>' + loadingLabel;
        try {
          await addItemsAndOpenCart([
            {
              id:       Number(intent.anchorVariantId),
              quantity: 1,
            },
            {
              id:       Number(companionVariantId),
              quantity: deal.maxQtyPerAnchor || 1,
            },
          ]);
          clearIntent();
          hideBar(); // cart is now open — dismiss the sticky bar
        } catch (err) {
          console.error('[StickyBundleBar] Add to cart failed:', err);
        } finally {
          // Always restore button regardless of success or failure.
          ctaBtn.disabled = false;
          ctaBtn.classList.remove(LOADING_CLASS);
          ctaBtn.innerHTML = originalHTML;
        }
      });
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function ensureBarInBody() {
    const bar = document.getElementById('sticky-bundle-bar');
    if (bar && bar.parentElement !== document.body) {
      document.body.appendChild(bar);
    }
  }

  function init() {
    // Always wire up anchor-page links (safe no-op if none exist)
    augmentAnchorLinks();

    // Escape the Shopify section stacking context so position:fixed works.
    ensureBarInBody();

    // Companion-page activation gate
    const meta = readMeta();
    if (!meta) return; // not an addon page

    const resolved = resolveIntent(meta);
    if (!resolved) return; // no valid bundle context — organic visitor

    const { intent, deal } = resolved;
    if (isDismissed(intent.anchorProductId, intent.companionProductId)) return;

    showBar(deal);
    bindHandlers(intent, deal);

    // Hide the bar whenever the cart is opened from any source
    // (e.g. anchor-page ATC, mini-cart icon click, etc.).
    document.documentElement.addEventListener('cart:refresh', () => hideBar(), { once: true });
  }

  window.StickyBundleBar = { init };

  if (!window[INIT_GUARD]) {
    window[INIT_GUARD] = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
